const API_URL = 'https://api.phuthaitech.com.vn/web/product-vouchers';
const isRecord = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const text = (value) => typeof value === 'string' ? value.trim() : '';
const nonnegative = (value) => typeof value === 'number' && Number.isFinite(value) && value >= 0;
const handles = (value) => Array.isArray(value) && value.every((item) => typeof item === 'string' && item.trim());

export const normalizeVoucher = (value) => {
  if (!isRecord(value) || !isRecord(value.value) || !isRecord(value.scope)) return null;
  const code = text(value.code).toUpperCase();
  const label = text(value.value_label);
  const { type, amount } = value.value;
  if (!code || !label || !['percentage', 'amount', 'shipping'].includes(type) || !nonnegative(amount)) return null;
  if ((type === 'percentage' && amount > 100) || (type === 'shipping' && amount !== 0)) return null;
  if (value.minimum_subtotal !== null && !nonnegative(value.minimum_subtotal)) return null;
  if (value.ends_at !== null && (typeof value.ends_at !== 'string' || !Number.isFinite(Date.parse(value.ends_at)))) return null;
  if (typeof value.scope.all !== 'boolean' || !handles(value.scope.collections) || !handles(value.scope.products)) return null;
  return {
    code, value: { type, amount }, value_label: label,
    minimum_subtotal: value.minimum_subtotal, ends_at: value.ends_at,
    scope: {
      all: value.scope.all,
      collections: value.scope.collections.map(text), products: value.scope.products.map(text)
    }
  };
};

export const appliesToProduct = (voucher, { handle, collections }) => voucher.scope.all
  || voucher.scope.products.includes(handle)
  || collections.some((collection) => voucher.scope.collections.includes(collection));

export const voucherSavings = (voucher, price) => {
  if (voucher.value.type === 'percentage') return price * voucher.value.amount / 100;
  return voucher.value.type === 'amount' ? voucher.value.amount : 0;
};

export const sortVouchers = (vouchers, price) => [...vouchers].sort((a, b) =>
  voucherSavings(b, price) - voucherSavings(a, price) || (a.code < b.code ? -1 : a.code > b.code ? 1 : 0));

export const chipText = (voucher, minLabel) => {
  const label = voucher.value_label.charAt(0).toLocaleLowerCase('vi-VN') + voucher.value_label.slice(1);
  const minimum = voucher.minimum_subtotal === null ? ''
    : ` (${minLabel} ${voucher.minimum_subtotal.toLocaleString('vi-VN')}₫)`;
  return `${voucher.code}: ${label}${minimum}`;
};

export const initProductVouchers = async (root) => {
  if (root.dataset.productVouchersReady === 'true') return;
  root.dataset.productVouchersReady = 'true';
  try {
    const response = await fetch(API_URL, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error(`Voucher request failed: ${response.status}`);
    const payload = await response.json();
    if (!Array.isArray(payload?.vouchers)) throw new Error('Voucher response is invalid');
    const product = {
      handle: text(root.dataset.productHandle),
      collections: (root.dataset.productCollections || '').split(',').map(text).filter(Boolean)
    };
    const now = Date.now();
    const vouchers = payload.vouchers.map(normalizeVoucher).filter((voucher) => voucher
      && (voucher.ends_at === null || Date.parse(voucher.ends_at) > now)
      && appliesToProduct(voucher, product));
    const template = root.parentElement.querySelector('[data-product-voucher-template]');
    const chips = sortVouchers(vouchers, Number(root.dataset.productPrice)).map((voucher) => {
      const chip = template.content.querySelector('li').cloneNode(true);
      chip.querySelector('span').textContent = chipText(voucher, root.dataset.minLabel || 'đơn từ');
      return chip;
    });
    if (chips.length) {
      root.prepend(...chips);
      root.removeAttribute('hidden');
    }
  } catch (error) {
    console.warn('Product vouchers unavailable', error);
  }
};

const initAll = (scope = document) => scope.querySelectorAll?.('[data-product-vouchers]').forEach(initProductVouchers);
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  else initAll();
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
}
